import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiCalendar, FiUsers, FiCreditCard, FiShield, FiCheck, FiWifi, FiCar, FiCoffee } = FiIcons;

function BookingPage() {
  const [bookingData, setBookingData] = useState({
    checkIn: '',
    checkOut: '',
    guests: 1,
    roomType: 'double',
    specialRequests: ''
  });

  const [step, setStep] = useState(1);

  const roomTypes = [
    {
      id: 'double',
      name: 'Double Room',
      price: 6500,
      capacity: '2 Guests',
      image: 'https://images.unsplash.com/photo-1618773928121-c32242e63f39?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80',
      features: ['Queen Bed', 'Garden View', 'AC', 'Hot Water', 'Free WiFi', 'Work Desk']
    },
    {
      id: 'family',
      name: 'Family Room',
      price: 8000,
      capacity: '4-5 Guests',
      image: 'https://images.unsplash.com/photo-1631049307264-da0ec9d70304?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80',
      features: ['Triple Beds', 'Mountain View', 'AC', 'Hot Water', 'Free WiFi', 'Mini Fridge']
    }
  ];

  const handleInputChange = (field, value) => {
    setBookingData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const calculateNights = () => {
    if (bookingData.checkIn && bookingData.checkOut) {
      const checkIn = new Date(bookingData.checkIn);
      const checkOut = new Date(bookingData.checkOut);
      const diffTime = Math.abs(checkOut - checkIn);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays;
    }
    return 1;
  };

  const calculateTotal = () => {
    const selectedRoom = roomTypes.find(room => room.id === bookingData.roomType);
    const nights = calculateNights();
    return selectedRoom ? selectedRoom.price * nights : 0;
  };

  const selectedRoom = roomTypes.find(room => room.id === bookingData.roomType);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-emerald-900 pt-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl md:text-5xl font-serif font-bold text-white mb-6">
            Book Your <span className="gradient-text">Stay</span>
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
            Reserve your basecamp for the ultimate Sinharaja Morning Side adventure. 
            Enjoy direct booking benefits and the best rates guaranteed.
          </p>

          {/* Benefits Bar */}
          <div className="glass-morphism rounded-xl p-4 inline-flex flex-wrap justify-center gap-4 text-sm">
            <div className="flex items-center space-x-2 text-emerald-400">
              <SafeIcon icon={FiCheck} className="w-4 h-4" />
              <span>No Booking Fees</span>
            </div>
            <div className="flex items-center space-x-2 text-emerald-400">
              <SafeIcon icon={FiCheck} className="w-4 h-4" />
              <span>Free Cancellation</span>
            </div>
            <div className="flex items-center space-x-2 text-emerald-400">
              <SafeIcon icon={FiCheck} className="w-4 h-4" />
              <span>Best Price Guarantee</span>
            </div>
          </div>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Booking Form */}
          <div className="lg:col-span-2">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2, duration: 0.8 }}
              className="glass-morphism rounded-2xl p-8"
            >
              {/* Step 1: Room Selection */}
              {step === 1 && (
                <div className="space-y-8">
                  <h2 className="text-2xl font-serif font-bold text-white mb-6">
                    1. Select Your Room
                  </h2>

                  <div className="grid gap-6">
                    {roomTypes.map((room) => (
                      <motion.div
                        key={room.id}
                        whileHover={{ scale: 1.02 }}
                        className={`border-2 rounded-xl p-6 cursor-pointer transition-all duration-300 ${
                          bookingData.roomType === room.id
                            ? 'border-emerald-400 bg-emerald-400/10'
                            : 'border-white/20 hover:border-white/40'
                        }`}
                        onClick={() => handleInputChange('roomType', room.id)}
                      >
                        <div className="grid md:grid-cols-3 gap-6">
                          <div className="relative">
                            <img
                              src={room.image}
                              alt={room.name}
                              className="w-full h-32 object-cover rounded-lg"
                            />
                            {bookingData.roomType === room.id && (
                              <div className="absolute top-2 right-2 w-6 h-6 bg-emerald-400 rounded-full flex items-center justify-center">
                                <SafeIcon icon={FiCheck} className="w-4 h-4 text-white" />
                              </div>
                            )}
                          </div>
                          
                          <div className="md:col-span-2 space-y-4">
                            <div className="flex justify-between items-start">
                              <div>
                                <h3 className="text-xl font-semibold text-white">{room.name}</h3>
                                <p className="text-gray-300">{room.capacity}</p>
                              </div>
                              <div className="text-right">
                                <div className="text-2xl font-bold text-emerald-400">LKR {room.price.toLocaleString()}</div>
                                <div className="text-sm text-gray-300">per night</div>
                              </div>
                            </div>
                            
                            <div className="grid grid-cols-2 gap-2">
                              {room.features.map((feature) => (
                                <div key={feature} className="flex items-center space-x-2 text-sm text-gray-300">
                                  <SafeIcon icon={FiCheck} className="w-3 h-3 text-emerald-400" />
                                  <span>{feature}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>

                  <button
                    onClick={() => setStep(2)}
                    className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-4 px-6 rounded-xl transition-colors duration-300"
                  >
                    Continue to Dates
                  </button>
                </div>
              )}

              {/* Step 2: Dates & Details */}
              {step === 2 && (
                <div className="space-y-8">
                  <div className="flex items-center justify-between">
                    <h2 className="text-2xl font-serif font-bold text-white">
                      2. Select Dates & Details
                    </h2>
                    <button
                      onClick={() => setStep(1)}
                      className="text-emerald-400 hover:text-emerald-300 transition-colors"
                    >
                      ← Back
                    </button>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    {/* Check-in */}
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Check-in Date
                      </label>
                      <div className="relative">
                        <input
                          type="date"
                          value={bookingData.checkIn}
                          onChange={(e) => handleInputChange('checkIn', e.target.value)}
                          min={new Date().toISOString().split('T')[0]}
                          className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-emerald-400"
                        />
                        <SafeIcon icon={FiCalendar} className="absolute right-3 top-3.5 w-5 h-5 text-gray-400" />
                      </div>
                    </div>

                    {/* Check-out */}
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Check-out Date
                      </label>
                      <div className="relative">
                        <input
                          type="date"
                          value={bookingData.checkOut}
                          onChange={(e) => handleInputChange('checkOut', e.target.value)}
                          min={bookingData.checkIn || new Date().toISOString().split('T')[0]}
                          className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-emerald-400"
                        />
                        <SafeIcon icon={FiCalendar} className="absolute right-3 top-3.5 w-5 h-5 text-gray-400" />
                      </div>
                    </div>
                  </div>

                  {/* Guests */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Number of Guests
                    </label>
                    <div className="relative">
                      <select
                        value={bookingData.guests}
                        onChange={(e) => handleInputChange('guests', parseInt(e.target.value))}
                        className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-emerald-400 appearance-none"
                      >
                        {[1, 2, 3, 4, 5].map(num => (
                          <option key={num} value={num} className="bg-slate-800">
                            {num} Guest{num > 1 ? 's' : ''}
                          </option>
                        ))}
                      </select>
                      <SafeIcon icon={FiUsers} className="absolute right-3 top-3.5 w-5 h-5 text-gray-400 pointer-events-none" />
                    </div>
                  </div>

                  {/* Special Requests */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Special Requests (Optional)
                    </label>
                    <textarea
                      value={bookingData.specialRequests}
                      onChange={(e) => handleInputChange('specialRequests', e.target.value)}
                      placeholder="Early check-in, late check-out, dietary requirements, etc."
                      rows={4}
                      className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-emerald-400"
                    />
                  </div>

                  <button
                    onClick={() => setStep(3)}
                    disabled={!bookingData.checkIn || !bookingData.checkOut}
                    className="w-full bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-semibold py-4 px-6 rounded-xl transition-colors duration-300"
                  >
                    Continue to Payment
                  </button>
                </div>
              )}

              {/* Step 3: Payment */}
              {step === 3 && (
                <div className="space-y-8">
                  <div className="flex items-center justify-between">
                    <h2 className="text-2xl font-serif font-bold text-white">
                      3. Complete Booking
                    </h2>
                    <button
                      onClick={() => setStep(2)}
                      className="text-emerald-400 hover:text-emerald-300 transition-colors"
                    >
                      ← Back
                    </button>
                  </div>

                  {/* Payment Options */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-white">Payment Options</h3>
                    
                    <div className="grid gap-4">
                      <div className="border border-white/20 rounded-lg p-4 hover:border-emerald-400 transition-colors cursor-pointer">
                        <div className="flex items-center space-x-3">
                          <SafeIcon icon={FiCreditCard} className="w-6 h-6 text-emerald-400" />
                          <div>
                            <div className="font-semibold text-white">Credit/Debit Card</div>
                            <div className="text-sm text-gray-300">Visa, Mastercard, American Express</div>
                          </div>
                        </div>
                      </div>
                      
                      <div className="border border-white/20 rounded-lg p-4 hover:border-emerald-400 transition-colors cursor-pointer">
                        <div className="flex items-center space-x-3">
                          <div className="w-6 h-6 bg-blue-500 rounded flex items-center justify-center text-white text-xs font-bold">
                            QR
                          </div>
                          <div>
                            <div className="font-semibold text-white">LankaQR</div>
                            <div className="text-sm text-gray-300">Instant payment via QR code</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* WhatsApp Booking Alternative */}
                  <div className="border-2 border-dashed border-emerald-400/50 rounded-lg p-6 text-center">
                    <h3 className="text-lg font-semibold text-white mb-3">
                      Prefer Personal Service?
                    </h3>
                    <p className="text-gray-300 mb-4">
                      Complete your booking via WhatsApp with our team for personalized assistance.
                    </p>
                    <a
                      href={`https://wa.me/94771234567?text=Hi! I'd like to book a ${selectedRoom?.name} for ${bookingData.checkIn} to ${bookingData.checkOut} for ${bookingData.guests} guests.`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center space-x-2 bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-6 rounded-xl transition-colors duration-300"
                    >
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488z"/>
                      </svg>
                      <span>Book via WhatsApp</span>
                    </a>
                  </div>
                </div>
              )}
            </motion.div>
          </div>

          {/* Booking Summary */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4, duration: 0.8 }}
            className="space-y-6"
          >
            {/* Summary Card */}
            <div className="glass-morphism rounded-2xl p-6">
              <h3 className="text-xl font-serif font-bold text-white mb-6">Booking Summary</h3>
              
              {selectedRoom && (
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-300">Room Type</span>
                    <span className="text-white font-medium">{selectedRoom.name}</span>
                  </div>
                  
                  {bookingData.checkIn && bookingData.checkOut && (
                    <>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-300">Dates</span>
                        <span className="text-white font-medium text-sm">
                          {new Date(bookingData.checkIn).toLocaleDateString()} - {new Date(bookingData.checkOut).toLocaleDateString()}
                        </span>
                      </div>
                      
                      <div className="flex justify-between items-center">
                        <span className="text-gray-300">Nights</span>
                        <span className="text-white font-medium">{calculateNights()}</span>
                      </div>
                    </>
                  )}
                  
                  <div className="flex justify-between items-center">
                    <span className="text-gray-300">Guests</span>
                    <span className="text-white font-medium">{bookingData.guests}</span>
                  </div>
                  
                  <div className="border-t border-white/20 pt-4">
                    <div className="flex justify-between items-center">
                      <span className="text-lg font-semibold text-white">Total</span>
                      <span className="text-2xl font-bold text-emerald-400">
                        LKR {calculateTotal().toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Amenities */}
            <div className="glass-morphism rounded-2xl p-6">
              <h3 className="text-lg font-semibold text-white mb-4">Included Amenities</h3>
              <div className="space-y-3">
                <div className="flex items-center space-x-3 text-gray-300">
                  <SafeIcon icon={FiWifi} className="w-5 h-5 text-emerald-400" />
                  <span>High-Speed WiFi (20Mbps)</span>
                </div>
                <div className="flex items-center space-x-3 text-gray-300">
                  <SafeIcon icon={FiCar} className="w-5 h-5 text-emerald-400" />
                  <span>Free Secure Parking</span>
                </div>
                <div className="flex items-center space-x-3 text-gray-300">
                  <SafeIcon icon={FiCoffee} className="w-5 h-5 text-emerald-400" />
                  <span>Welcome Drink</span>
                </div>
                <div className="flex items-center space-x-3 text-gray-300">
                  <SafeIcon icon={FiShield} className="w-5 h-5 text-emerald-400" />
                  <span>24/7 Front Desk</span>
                </div>
              </div>
            </div>

            {/* Policies */}
            <div className="glass-morphism rounded-2xl p-6">
              <h3 className="text-lg font-semibold text-white mb-4">Booking Policies</h3>
              <div className="space-y-2 text-sm text-gray-300">
                <p>✓ Free cancellation up to 24 hours before check-in</p>
                <p>✓ Check-in: 2:00 PM | Check-out: 11:00 AM</p>
                <p>✓ Early check-in/late check-out available on request</p>
                <p>✓ No prepayment required - pay at property</p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}

export default BookingPage;